import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import CommandScene from "@/components/CommandScene";
import SpaceNoiseListener from "@/components/SpaceNoiseListener";
import api, { getToken, formatErr } from "@/lib/api";
import { useLang } from "@/i18n";
import { Download, Radio, Activity, Compass, Gauge, Clock, Signal, Crosshair, Zap, Lock, ShieldCheck, Send, CreditCard, FileCheck2, Share2, Archive, Hexagon } from "lucide-react";

const STAGE_FLOW_FALLBACK = [
  "MISSION_STARTED",
  "SIGNAL_LEAVING_EARTH",
  "PASSING_LUNAR_ORBIT",
  "LEAVING_SOLAR_SYSTEM",
  "CROSSING_HELIOSPHERE",
  "ENTERING_INTERSTELLAR_SPACE",
  "APPROACHING_DESTINATION",
  "MISSION_COMPLETE",
];

const fmtLightTime = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s.toFixed(2)} s`;
  if (s < 3600) return `${(s / 60).toFixed(2)} min`;
  if (s < 86400) return `${(s / 3600).toFixed(2)} h`;
  if (s < 31557600) return `${(s / 86400).toFixed(2)} d`;
  return `${(s / 31557600).toFixed(2)} yr`;
};

const fmtBytes = (b) => { const u=["B","KB","MB","GB","TB"]; let i=0,n=b||0; while(n>=1024&&i<u.length-1){n/=1024;i++} return `${n.toFixed(n>=100?0:1)} ${u[i]}`;};

const fmtKm = (km) => {
  if (!km && km !== 0) return "—";
  if (km >= 1e12) return `${(km / 1e12).toFixed(3)} ×10¹² km`;
  if (km >= 1e9) return `${(km / 1e9).toFixed(2)} ×10⁹ km`;
  if (km >= 1e6) return `${(km / 1e6).toFixed(2)} ×10⁶ km`;
  return `${km.toFixed(0)} km`;
};

const fmtDuration = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
};

const TelemetryCard = ({ icon: Icon, label, value, sub, testid, accent = "#00D4FF" }) => (
  <div className="holo-panel p-4 relative" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-overline" style={{ color: accent }}>{label}</div>
      <Icon size={14} style={{ color: accent }} />
    </div>
    <div className="font-display font-black text-xl tracking-tighter mt-2 text-cyan-glow">{value}</div>
    {sub && <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/55 mt-1">{sub}</div>}
  </div>
);

export default function CommandCenter() {
  const { t, lang } = useLang();
  const { tid } = useParams();
  const [tx, setTx] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [err, setErr] = useState("");
  const progressRef = useRef(0);
  const [payWallets, setPayWallets] = useState(null);
  const [payMethod, setPayMethod] = useState("pix");
  const [txRef, setTxRef] = useState("");
  const [payAmount, setPayAmount] = useState(49);
  const [payBusy, setPayBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get(`/transmissions/${tid}`).then((r) => { if (!cancel) setTx(r.data); }).catch((e) => setErr(formatErr(e)));
    api.get(`/transmissions/${tid}/payment`).then((r) => { if (!cancel) { setPayWallets(r.data.wallets); setPayAmount(r.data.amount_usd); } }).catch(() => {});
    return () => { cancel = true; };
  }, [tid]);

  useEffect(() => {
    let interval;
    const poll = async () => {
      try {
        const { data } = await api.get(`/transmissions/${tid}/telemetry`);
        setTelemetry(data);
        progressRef.current = data.progress || 0;
      } catch (_) {}
    };
    poll();
    interval = setInterval(poll, 1000);
    return () => clearInterval(interval);
  }, [tid]);

  const [autoApproved, setAutoApproved] = useState(false);
  const submitPayment = async () => {
    setPayBusy(true); setErr(""); setAutoApproved(false);
    try {
      const { data } = await api.post(`/transmissions/${tid}/pay`, { method: payMethod, tx_ref: txRef });
      const { data: txData } = await api.get(`/transmissions/${tid}`); setTx(txData);
      if (data?.auto_approved) {
        setAutoApproved(true);
        setErr("");
      } else if (data?.verification && data.verification.reason && data.verification.reason !== "not_attempted") {
        const reason = data.verification.reason;
        setErr(
          (lang === "pt"
            ? "Comprovante recebido — verificação on-chain ainda não conclusiva: "
            : "Proof received — on-chain verification not yet conclusive: ")
          + reason
          + (lang === "pt" ? ". Operações revisará manualmente." : ". Operations will review manually."),
        );
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setPayBusy(false); }
  };

  const upgradeToReal = async () => {
    setPayBusy(true); setErr("");
    try {
      await api.post(`/transmissions/${tid}/upgrade-to-real`);
      const { data } = await api.get(`/transmissions/${tid}`); setTx(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setPayBusy(false); }
  };

  const paymentStatus = telemetry?.payment_status || tx?.payment_status || "preview";
  const realTransmission = telemetry?.real_transmission || tx?.real_transmission || false;
  const isLocked = telemetry?.locked === true;
  const isComplete = telemetry?.is_complete === true || telemetry?.status === "MISSION_COMPLETE";
  const previewComplete = isComplete && !realTransmission && paymentStatus !== "approved";
  const realRequested = realTransmission && paymentStatus !== "approved";
  const dest = tx?.destination || telemetry?.destination;
  // AI-personalized narrative stages drive the timeline (falls back to legacy STAGE_FLOW)
  const narrative = telemetry?.narrative || tx?.narrative;
  const narrativeStages = (narrative?.stages && narrative.stages.length >= 3) ? narrative.stages : null;
  const flowKeys = narrativeStages ? narrativeStages.map((s) => s.key) : STAGE_FLOW_FALLBACK;
  const currentStatus = telemetry?.status || tx?.status || flowKeys[0];
  const currentIdx = Math.max(0, flowKeys.indexOf(currentStatus));
  const certUrl = `${process.env.REACT_APP_BACKEND_URL}/api/transmissions/${tid}/certificate?auth=${encodeURIComponent(getToken() || "")}`;

  const distanceLy = telemetry?.distance_total_ly ?? dest?.distance_ly ?? 0;
  const distanceKm = telemetry?.distance_total_km ?? distanceLy * 9.461e12;
  const traveledKm = telemetry?.distance_traveled_km ?? 0;
  const remainingKm = telemetry?.distance_remaining_km ?? distanceKm;
  const sector = telemetry?.current_sector ?? (isLocked ? (lang === "pt" ? "AGUARDANDO LIBERAÇÃO" : "AWAITING CLEARANCE") : "EARTH ORBIT");
  const signalStrength = telemetry?.signal_strength_db ?? -30;
  const eta = telemetry?.eta_seconds;
  const progressPct = (telemetry?.progress ?? 0) * 100;
  const wallet = payWallets && payWallets[payMethod];

  return (
    <AppShell>
      <div className="mb-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">{lang === "pt" ? "Controle de Missão · Tempo Real" : "Mission Control · Live"}</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1" data-testid="cmd-title">
            {dest ? (lang === "pt" ? `Rota → ${dest.name}` : `Route → ${dest.name}`) : (lang === "pt" ? "Estabelecendo Rota…" : "Establishing Route…")}
          </h1>
          <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-1" data-testid="cmd-tid">
            {tx?.transmission_id} · {tx?.mission_number}
          </div>
        </div>
        <div className="holo-panel px-4 py-2 flex items-center gap-3">
          <Radio size={14} className="text-[#00D4FF] blink" />
          <span className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-[#00D4FF]" data-testid="cmd-sector">{sector}</span>
        </div>
      </div>

      {/* Premium MISSION LAUNCHED · SIGNAL IN TRANSIT — appears whenever the mission has completed (preview or real) */}
      {isComplete && tx?.certificate_id && (
        <MissionLaunchedPanel
          lang={lang}
          tx={tx}
          certUrl={certUrl}
          isReal={realTransmission && paymentStatus === "approved"}
        />
      )}

      {/* Mission Complete (Preview) summary + Upgrade to Real */}
      {previewComplete && (
        <div className="holo-panel p-6 mb-4 border-[#62FFB0]/30" data-testid="preview-complete-panel">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-12 h-12 grid place-items-center border border-[#62FFB0]/40 shrink-0">
              <ShieldCheck size={18} className="text-[#62FFB0]" />
            </div>
            <div className="flex-1 min-w-[280px]">
              <div className="label-overline text-[#62FFB0]">
                {lang === "pt" ? "Preview da Missão Concluído" : "Mission Preview Complete"}
              </div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1">
                {lang === "pt"
                  ? `Trajeto até ${dest?.name} simulado com sucesso`
                  : `Simulated trajectory to ${dest?.name} complete`}
              </div>
              <p className="text-white/70 text-sm mt-2 max-w-2xl leading-relaxed">
                {lang === "pt"
                  ? `Você assistiu sua transmissão atravessar o espaço até ${dest?.name}. Para transformar este preview em uma TRANSMISSÃO REAL através do relé Amazon Kuiper com disparo laser, efetue o pagamento de US$ ${payAmount}. Após confirmação, a missão é liberada manualmente pela equipe de operações e o sinal é emitido de fato.`
                  : `You've watched your transmission cross space to ${dest?.name}. To turn this preview into a REAL transmission via the Amazon Kuiper laser relay, complete a $${payAmount} payment. After confirmation, mission operations releases the broadcast and the signal is actually emitted.`}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-4 text-[12px]">
                <div className="border border-white/5 p-3">
                  <div className="label-overline text-[10px]">Callsign</div>
                  <div className="font-mono-data text-[#00D4FF] mt-1">{tx?.callsign}</div>
                </div>
                <div className="border border-white/5 p-3">
                  <div className="label-overline text-[10px]">{lang === "pt" ? "Pacote" : "Package"}</div>
                  <div className="font-mono-data text-[#00D4FF] mt-1">{fmtBytes(tx?.package_size || 0)}</div>
                </div>
                <div className="border border-white/5 p-3 sm:col-span-2">
                  <div className="label-overline text-[10px]">{lang === "pt" ? "Banda de Frequência" : "Frequency Band"}</div>
                  <div className="font-mono-data text-[11px] text-white/75 mt-1">{tx?.personalized?.frequency_band}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-5">
                <button
                  onClick={upgradeToReal}
                  disabled={payBusy}
                  data-testid="upgrade-to-real"
                  className="btn-primary-s2s !text-[11px] disabled:opacity-50"
                >
                  <CreditCard size={14} />
                  {lang === "pt" ? `TRANSMITIR DE VERDADE · US$ ${payAmount}` : `TRANSMIT FOR REAL · $${payAmount}`}
                </button>
              </div>
              {err && <div className="mt-3 text-[#FF3366] font-mono-data text-xs">{err}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Payment + admin release gating (only when user opted into real transmission) */}
      {realRequested && (
        <div className="holo-panel p-6 mb-4 border-[#00D4FF]/40" data-testid="payment-lock-panel">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 grid place-items-center border border-[#00D4FF]/40 shrink-0">
              <Lock size={18} className="text-[#00D4FF]" />
            </div>
            <div className="flex-1">
              <div className="label-overline" data-testid="payment-status-label">
                {paymentStatus === "unpaid" && (lang === "pt" ? "Aguardando Pagamento" : "Awaiting Payment")}
                {paymentStatus === "pending_review" && (lang === "pt" ? "Aguardando Aprovação da Missão" : "Awaiting Mission Approval")}
                {paymentStatus === "rejected" && (lang === "pt" ? "Pagamento Rejeitado" : "Payment Rejected")}
              </div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1">
                {paymentStatus === "unpaid" && (lang === "pt"
                  ? `Libere a transmissão · US$ ${payAmount}`
                  : `Release the transmission · $${payAmount}`)}
                {paymentStatus === "pending_review" && (lang === "pt"
                  ? "Operações verificando…"
                  : "Mission Operations verifying…")}
                {paymentStatus === "rejected" && (lang === "pt"
                  ? "Entre em contato com operações"
                  : "Please contact mission operations")}
              </div>
              <p className="text-white/65 text-sm mt-2 max-w-2xl">
                {lang === "pt"
                  ? "Sua transmissão é única, exclusiva e assinada digitalmente para o destino escolhido. Será liberada manualmente pela equipe de operações após confirmação do pagamento."
                  : "Your transmission is unique, exclusive and digitally signed for the chosen destination. It will be released manually by mission operations after payment confirmation."}
              </p>

              {paymentStatus === "unpaid" && payWallets && (
                <div className="mt-5 space-y-4" data-testid="payment-form">
                  <div className="flex flex-wrap gap-2">
                    {["pix", "usdt", "btc"].map((m) => (
                      <button
                        key={m}
                        onClick={() => setPayMethod(m)}
                        data-testid={`pay-method-${m}`}
                        className={`px-4 py-2 font-mono-data text-[11px] tracking-[0.2em] uppercase border ${
                          payMethod === m ? "border-[#00D4FF] bg-[#00D4FF] text-[#03040B]" : "border-white/15 hover:border-white/40"
                        }`}
                      >
                        {m === "pix" ? "PIX (BRL)" : m === "usdt" ? "USDT (TRC20)" : "BTC"}
                      </button>
                    ))}
                  </div>
                  <div>
                    <div className="label-overline">{lang === "pt" ? "Carteira de Destino" : "Send to"}</div>
                    <div className="font-mono-data text-sm text-[#00D4FF] mt-1 break-all" data-testid="pay-wallet">
                      {wallet?.label} · {wallet?.value}
                    </div>
                    {(payMethod === "btc" || payMethod === "usdt") && (
                      <div className="mt-2 font-mono-data text-[10px] tracking-[0.16em] uppercase text-[#62FFB0]/80" data-testid="pay-onchain-hint">
                        {lang === "pt"
                          ? "Verificação on-chain automática · liberação instantânea após confirmação"
                          : "Auto on-chain verification · instant release once confirmed"}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="label-overline">{lang === "pt" ? "Referência da Transação" : "Transaction Reference"}</label>
                    <input
                      value={txRef}
                      onChange={(e) => setTxRef(e.target.value)}
                      data-testid="pay-txref"
                      className="w-full mt-2 bg-transparent border-b border-[#00D4FF]/25 focus:border-[#00D4FF] outline-none py-2 text-white font-mono-data text-sm"
                      placeholder={lang === "pt" ? "ID da transação / hash" : "Transaction ID / hash"}
                    />
                  </div>
                  <button
                    onClick={submitPayment}
                    disabled={!txRef || payBusy}
                    data-testid="pay-submit"
                    className="btn-primary-s2s !py-3 !text-[11px] disabled:opacity-50"
                  >
                    <CreditCard size={14} /> {lang === "pt" ? "ENVIAR COMPROVANTE" : "SUBMIT PROOF"}
                  </button>
                </div>
              )}

              {paymentStatus === "pending_review" && (
                <div className="mt-5 flex items-center gap-3 text-[#FFB800]" data-testid="payment-pending-review">
                  <Send size={16} className="blink" />
                  <span className="font-mono-data text-[12px] tracking-[0.18em] uppercase">
                    {lang === "pt"
                      ? "Verificação em andamento · A missão será liberada por um Comandante"
                      : "Verification in progress · A Commander will release the mission"}
                  </span>
                </div>
              )}

              {paymentStatus === "approved" && (
                <div className="mt-4 flex items-center gap-3 text-[#62FFB0]" data-testid="payment-approved">
                  <ShieldCheck size={16} /> <span className="font-mono-data text-sm">
                    {autoApproved
                      ? (lang === "pt" ? "VERIFICADO ON-CHAIN · TRANSMITINDO" : "ON-CHAIN VERIFIED · TRANSMITTING")
                      : "CLEARED · TRANSMITTING"}
                  </span>
                </div>
              )}

              {err && <div className="mt-3 text-[#FF3366] font-mono-data text-xs">{err}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Main 3D + side telemetry */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 holo-panel p-0 relative overflow-hidden scan-line" style={{ height: 520 }} data-testid="cmd-3d">
          <div className="absolute inset-0">
            <CommandScene destination={dest} progressRef={progressRef} visual={tx?.visual || telemetry?.visual} />
          </div>
          {/* Overlays */}
          <div className="absolute top-4 left-4 holo-panel px-3 py-2">
            <div className="label-overline">{lang === "pt" ? "Origem" : "Origin"}</div>
            <div className="font-mono-data text-sm text-[#00D4FF] mt-0.5">EARTH</div>
            <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/55 mt-0.5">SOL · 1 AU</div>
          </div>
          <div className="absolute top-4 right-4 holo-panel px-3 py-2 text-right">
            <div className="label-overline">{lang === "pt" ? "Destino" : "Destination"}</div>
            <div className="font-mono-data text-sm text-[#00D4FF] mt-0.5">{dest?.name || "—"}</div>
            <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/55 mt-0.5">{dest?.constellation}</div>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="holo-panel px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="label-overline">{lang === "pt" ? "Progresso da Missão" : "Mission Progress"}</div>
                <div className="font-mono-data text-[#00D4FF] text-sm">{progressPct.toFixed(1)}%</div>
              </div>
              <div className="mt-2 h-1.5 bg-white/5 relative overflow-hidden">
                <div className="absolute left-0 top-0 h-full bg-[#00D4FF]" style={{ width: `${progressPct}%`, transition: "width 0.8s linear" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Mission timeline (AI-personalized narrative when present) */}
        <div className="holo-panel p-5 flex flex-col" data-testid="cmd-timeline">
          <div className="label-overline mb-2 flex items-center gap-2">
            <Compass size={12} /> {lang === "pt" ? "Trajetória Única" : "Unique Trajectory"}
          </div>
          {narrative?.intro && (
            <p className="text-white/65 text-[12px] leading-relaxed mb-3" data-testid="cmd-narrative-intro">
              {narrative.intro}
            </p>
          )}
          <div className="space-y-3 flex-1 overflow-auto pr-1" data-testid="cmd-narrative-stages">
            {(narrativeStages || STAGE_FLOW_FALLBACK.map((k) => ({ key: k, label: t(`cmd.status_${k}`), narrative: "" }))).map((s, i) => {
              const reached = i <= currentIdx;
              const isCurrent = i === currentIdx && !isComplete;
              return (
                <div key={s.key + i} className="flex items-start gap-3" data-testid={`stage-${s.key}`}>
                  <div className={`mt-1.5 w-2 h-2 rounded-full ${reached ? "bg-[#00D4FF]" : "bg-white/10"} ${isCurrent ? "blink" : ""}`} />
                  <div className="flex-1">
                    <div className={`font-mono-data text-[11px] tracking-[0.14em] uppercase ${reached ? "text-white" : "text-white/35"}`}>
                      {s.label || s.key.replace(/_/g, " ")}
                    </div>
                    {s.narrative && (
                      <div className={`text-[11px] leading-relaxed mt-1 ${reached ? "text-white/70" : "text-white/25"}`}>
                        {s.narrative}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="divider-cyan my-4" />
          {isComplete ? (
            <div>
              <div className="font-display font-black text-lg text-[#62FFB0] tracking-tighter" data-testid="cmd-complete">
                {narrative?.outro || t("cmd.done")}
              </div>
              <a
                href={certUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-3 btn-primary-s2s w-full justify-center !py-3 !text-[11px]"
                data-testid="cmd-download-cert"
              >
                <Download size={14} /> {t("cmd.cert")}
              </a>
            </div>
          ) : (
            <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55">
              ETA · <span className="text-[#00D4FF]">{fmtDuration(eta)}</span>
            </div>
          )}
          {err && <div className="mt-3 text-[#FF3366] font-mono-data text-xs">{err}</div>}
        </div>
      </div>

      {/* Telemetry strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4" data-testid="cmd-telemetry">
        <TelemetryCard
          icon={Gauge}
          label={lang === "pt" ? "Velocidade do Sinal" : "Signal Velocity"}
          value="299,792,458"
          sub="m/s · speed of light"
          testid="tel-velocity"
        />
        <TelemetryCard
          icon={Activity}
          label={lang === "pt" ? "Distância Percorrida" : "Distance Traveled"}
          value={fmtKm(traveledKm)}
          sub={`${(distanceLy * (telemetry?.progress ?? 0)).toFixed(3)} ly`}
          testid="tel-traveled"
        />
        <TelemetryCard
          icon={Crosshair}
          label={lang === "pt" ? "Distância Restante" : "Distance Remaining"}
          value={fmtKm(remainingKm)}
          sub={`${(distanceLy * (1 - (telemetry?.progress ?? 0))).toFixed(3)} ly`}
          testid="tel-remaining"
        />
        <TelemetryCard
          icon={Clock}
          label={lang === "pt" ? "Tempo Estimado" : "Estimated Arrival"}
          value={fmtDuration(eta)}
          sub={lang === "pt" ? "compressão visual" : "compressed timeline"}
          testid="tel-eta"
        />
        <TelemetryCard
          icon={Signal}
          label={lang === "pt" ? "Força do Sinal" : "Signal Strength"}
          value={`${signalStrength.toFixed(1)} dB`}
          sub={lang === "pt" ? "telemetria contínua" : "live telemetry"}
          testid="tel-signal"
        />
        <TelemetryCard
          icon={Clock}
          label={lang === "pt" ? "Atraso do Sinal (one-way)" : "Signal One-Way Delay"}
          value={fmtLightTime(telemetry?.signal_delay_oneway_s)}
          sub={lang === "pt"
            ? `Ida-volta: ${fmtLightTime(telemetry?.signal_round_trip_s)}`
            : `Round-trip: ${fmtLightTime(telemetry?.signal_round_trip_s)}`}
          testid="tel-delay"
        />
        <TelemetryCard
          icon={Zap}
          label={lang === "pt" ? "Path Loss (1550nm)" : "Path Loss (1550nm)"}
          value={`${(telemetry?.signal_path_loss_db ?? 0).toFixed(1)} dB`}
          sub={lang === "pt"
            ? `Rx: ${telemetry?.received_signal_dbm ?? "—"} dBm`
            : `Rx: ${telemetry?.received_signal_dbm ?? "—"} dBm`}
          testid="tel-fspl"
          accent="#FFB066"
        />
        <TelemetryCard
          icon={Compass}
          label={lang === "pt" ? "Setor Galáctico" : "Galactic Sector"}
          value={sector}
          sub={`RA ${(dest?.ra_deg ?? 0).toFixed(2)}° · DEC ${(dest?.dec_deg ?? 0).toFixed(2)}°`}
          testid="tel-sector"
        />
        <TelemetryCard
          icon={Zap}
          label={lang === "pt" ? "Tempo Real de Sinal" : "Real Signal Travel"}
          value={fmtLightTime(telemetry?.real_light_seconds)}
          sub={lang === "pt" ? "tempo real à velocidade da luz" : "actual time at speed of light"}
          testid="tel-real-years"
          accent="#62FFB0"
        />
        <TelemetryCard
          icon={Radio}
          label={lang === "pt" ? "Tamanho do Pacote" : "Package Size"}
          value={fmtBytes(tx?.package_size || 0)}
          sub={`SHA-512 · ${(tx?.digital_hash || "").slice(0, 12)}…`}
          testid="tel-package"
        />
      </div>

      {/* Space Listener — captures simulated cosmic noise + decoded signals from destination */}
      {dest && (
        <div className="mt-4" data-testid="cmd-space-listener-wrap">
          <SpaceNoiseListener destination={dest} lang={lang} />
        </div>
      )}
    </AppShell>
  );
}

// ──────────────────────────────────────────────────────────────
// Premium MISSION LAUNCHED · SIGNAL IN TRANSIT panel
// ──────────────────────────────────────────────────────────────
function MissionLaunchedPanel({ lang, tx, certUrl, isReal }) {
  const [sealed, setSealed] = useState(false);
  const [shared, setShared] = useState(false);
  const verifyUrl = `${window.location.origin}/verify/cert/${tx.certificate_id}`;

  useEffect(() => {
    const t = setTimeout(() => setSealed(true), 700);
    return () => clearTimeout(t);
  }, []);

  const onShare = async () => {
    const text = lang === "pt"
      ? `Minha cápsula foi transmitida para ${tx?.destination?.name} via S2S Sounds To Space. Certificado: ${verifyUrl}`
      : `My capsule was transmitted to ${tx?.destination?.name} via S2S Sounds To Space. Certificate: ${verifyUrl}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "S2S Intergalactic Certificate", text, url: verifyUrl });
      } else {
        await navigator.clipboard.writeText(verifyUrl);
      }
      setShared(true);
      setTimeout(() => setShared(false), 2200);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="holo-panel relative overflow-hidden p-8 mb-4" data-testid="mission-launched-panel"
         style={{ borderColor: "#FFD27A40", background: "linear-gradient(135deg, #0A0F1C 0%, #0a0f24 100%)" }}>
      {/* gold corner accents */}
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #FFD27A, transparent)" }} />
      <div className="absolute inset-x-0 bottom-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #00D4FF, transparent)" }} />
      {/* faint galaxy spiral background */}
      <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full pointer-events-none"
           style={{ background: "radial-gradient(closest-side, #FFD27A22, transparent 70%)" }} />

      <div className="flex items-start gap-6 flex-wrap relative">
        {/* Animated S2S seal */}
        <div className="relative shrink-0">
          <div className={`transition-all duration-700 ${sealed ? "scale-100 opacity-100 rotate-0" : "scale-150 opacity-0 -rotate-12"}`}>
            <div className="relative w-24 h-24 grid place-items-center">
              <Hexagon size={96} strokeWidth={1} className="text-[#FFD27A]" />
              <Hexagon size={72} strokeWidth={0.8} className="text-[#00D4FF] absolute" />
              <div className="absolute font-mono-data text-[10px] text-[#FFD27A] font-bold tracking-[0.2em]">S2S</div>
              <div className="absolute mt-7 font-mono-data text-[7px] text-white/70 tracking-[0.18em]">SEALED</div>
            </div>
          </div>
          {sealed && (
            <div className="absolute inset-0 rounded-full pointer-events-none"
                 style={{ animation: "sealPulse 1.4s ease-out forwards", boxShadow: "0 0 0 0 #FFD27A66" }} />
          )}
        </div>

        <div className="flex-1 min-w-[280px]">
          <div className="label-overline text-[#FFD27A] flex items-center gap-2">
            <ShieldCheck size={12} /> {isReal
              ? (lang === "pt" ? "MISSÃO REAL LANÇADA · SINAL EMITIDO" : "REAL MISSION LAUNCHED · SIGNAL EMITTED")
              : (lang === "pt" ? "PREVIEW DE MISSÃO CONCLUÍDO" : "MISSION PREVIEW COMPLETE")}
          </div>
          <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-2 leading-[1.05]">
            {lang === "pt" ? "MISSÃO LANÇADA" : "MISSION LAUNCHED"}
          </div>
          <div className="font-mono-data text-sm tracking-[0.18em] uppercase text-[#62FFB0] mt-1 flex items-center gap-2">
            <Radio size={12} className="blink" /> {lang === "pt" ? "SINAL EM TRÂNSITO" : "SIGNAL IN TRANSIT"}
          </div>
          <p className="text-white/75 text-sm mt-3 max-w-2xl leading-relaxed">
            {lang === "pt"
              ? "Sua Certidão Intergaláctica de Transmissão está pronta. Documento oficial criptografado, autenticado e armazenado de forma permanente. Seu sinal está agora viajando para além da Terra."
              : "Your Intergalactic Transmission Certificate is ready. Officially encrypted, authenticated and permanently registered. Your signal is now traveling beyond Earth."}
          </p>

          {/* Cert ID display */}
          <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-2xl">
            <div className="border border-[#FFD27A]/25 p-3 bg-black/30">
              <div className="label-overline text-[10px] text-[#FFD27A]">CERTIFICATE ID</div>
              <div className="font-mono-data text-xs text-white mt-1 break-all">{tx.certificate_id}</div>
            </div>
            <div className="border border-[#00D4FF]/25 p-3 bg-black/30">
              <div className="label-overline text-[10px]">MISSION ID</div>
              <div className="font-mono-data text-xs text-[#00D4FF] mt-1 break-all">{tx.mission_number || tx.transmission_id}</div>
            </div>
          </div>

          {/* 5 premium action buttons */}
          <div className="flex flex-wrap gap-3 mt-5">
            <a href={certUrl} target="_blank" rel="noreferrer"
               className="btn-primary-s2s !text-[11px]" data-testid="download-cert-premium">
              <Download size={14} />
              {lang === "pt" ? "BAIXAR CERTIDÃO PDF" : "DOWNLOAD INTERGALACTIC CERTIFICATE"}
            </a>
            <a href={verifyUrl} target="_blank" rel="noreferrer"
               className="btn-secondary-s2s !text-[11px]" data-testid="view-cert-premium">
              <FileCheck2 size={14} />
              {lang === "pt" ? "VER CERTIFICADO" : "VIEW CERTIFICATE"}
            </a>
            <a href={verifyUrl} target="_blank" rel="noreferrer"
               className="btn-secondary-s2s !text-[11px]" data-testid="verify-cert-premium">
              <ShieldCheck size={14} />
              {lang === "pt" ? "VERIFICAR" : "VERIFY CERTIFICATE"}
            </a>
            <button onClick={onShare}
                    className="btn-secondary-s2s !text-[11px]" data-testid="share-mission-premium">
              <Share2 size={14} />
              {shared
                ? (lang === "pt" ? "LINK COPIADO" : "LINK COPIED")
                : (lang === "pt" ? "COMPARTILHAR" : "SHARE MISSION")}
            </button>
            <a href="/vault" className="btn-secondary-s2s !text-[11px]" data-testid="store-vault-premium">
              <Archive size={14} />
              {lang === "pt" ? "GUARDAR NO COFRE" : "STORE IN SPACE VAULT"}
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sealPulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 210, 122, 0.6); }
          100% { box-shadow: 0 0 0 64px rgba(255, 210, 122, 0); }
        }
      `}</style>
    </div>
  );
}
